import { Shop_CoinBar } from './Shop_CoinBar';

/* eslint-disable no-unused-vars */

export const SHOP_EVENT_KEYS = {
  POSTCARD: 'POSTCARD',
};

export default class ShopUtils extends Phaser.Group {
  /**
   * filters all items from the catalog matching the current tier
   * @param {Array.<Object>} catalog
   * @returns {Array.<Object>}
   */
  static filterCurrentTier(catalog) {
    const availableDeals = catalog.filter((gift) => {
      const splitID = gift.productID.split('_');
      if (splitID[0] !== `t${OMT.envData.settings.env.tier}`) {
        return false;
      }
      return true;
    }, this);

    availableDeals.sort(
      (a, b) => parseFloat(a.price.replace(/^\D*/g, '').replace(',', '')) - parseFloat(b.price.replace(/^\D*/g, '').replace(',', '')),
    );
    return availableDeals;
  }

  /**
   * filter deals containing only coins
   * @param {Array} catalog
   * @returns {Array.<Object>}
   */
  static filterCoinDeals(catalog) {
    const availableDeals = catalog.filter((gift) => {
      const splitID = gift.productID.split('_');
      if (splitID[1] === 'coins') return true;
      return false;
    }, this);
    return availableDeals;
  }

  /**
   * filter popup offers
   * @param {Array.<Object>} catalog
   * @param {Object} runningEvent
   * @returns {Array.<Object>}
   */
  static filterPopupOffers(catalog, runningEvent = null) {
    if (!catalog || catalog.length === 0) return [];
    catalog = ShopUtils.filterCurrentTier(catalog);

    const eventDeals = catalog.filter((gift) => {
      const splitID = gift.productID.split('_');
      if (runningEvent && runningEvent.key === SHOP_EVENT_KEYS.POSTCARD
        && splitID[1] === runningEvent.packageId.toLowerCase()) {
        return true;
      }
      return false;
    });

    const normalDeals = catalog.filter((gift) => {
      const splitID = gift.productID.split('_');
      if (splitID[1] === 'popup') return true;
      return false;
    });

    const availableDeals = eventDeals.length > 0 ? eventDeals : normalDeals;
    availableDeals.sort((a, b) => parseFloat(a.price.replace(/^\D*/g, '')) - parseFloat(b.price.replace(/^\D*/g, '')));
    return availableDeals;
  }

  /**
   * returns the current special deal, based on the shopSeed (which is closely related to the year+month+day of the next special deal)
   * @param {Array.<Object>} catalog
   * @param {Object} runningEvent
   * @returns {Object}
   */
  static getSpecialDeal(catalog, runningEvent) {
    if (!G.saveState.sessionData.shopSpecialType) {
      const IAPCount = G.saveState.getIAPCount(); // Check how many times they've bought
      if (IAPCount > 0) {
        if (runningEvent) {
          G.saveState.sessionData.shopSpecialType = runningEvent.packageId.toLowerCase();
        } else {
          G.saveState.sessionData.shopSpecialType = 'special';
        }
      } else {
        if (runningEvent) { // eslint-disable-line no-lonely-if
          G.saveState.sessionData.shopSpecialType = runningEvent.firstTimeBuyerId.toLowerCase();
        } else {
          G.saveState.sessionData.shopSpecialType = 'first';
        }
      }
    }

    let availableDeals = catalog.filter((gift) => { // Filter for packages
      const splitID = gift.productID.split('_')[1];
      if (splitID === G.saveState.sessionData.shopSpecialType) {
        return true;
      }
      return false;
    }, this);
    if (availableDeals.length === 0) return null; // If nothing, then nothing

    const sortDeals = () => { // Sort the deals alphabetically... later
      availableDeals = availableDeals.sort((a, b) => {
        if (a.title > b.title) { return 1; }
        if (a.title < b.title) { return -1; }
        return 0;
      });
    };

    if (!G.saveState.sessionData.shopSeed) { // If theres no shop seed, we need to make one and keep it the same as previous
      game.rnd.sow([G.saveState.getTimeToNextSpecialDeal()]); // Use old formula for shop deals
      const originalDeal = game.rnd.pick(availableDeals); // Find the original picked deal
      sortDeals(); // Sort now
      G.saveState.sessionData.shopSeed = availableDeals.indexOf(originalDeal); // Find the deal
    } else {
      sortDeals(); // otherwise, just sort
    }
    const retValue = availableDeals[G.saveState.sessionData.shopSeed % availableDeals.length]; // Get the deal

    retValue.fullPrice = '€25.00'; // Find its full price. Put in temp for now
    const splitID = retValue.productID.split('_');
    splitID[1] += 'fullprice';
    const fullPriceID = splitID.join('_');

    for (let i = 0; i < catalog.length; i++) {
      if (catalog[i].productID === fullPriceID) {
        retValue.fullPrice = catalog[i].price; // Full price found
        break;
      }
    }

    return retValue;
  }

  /**
   * tries to purchase an item
   * @param {Object} item to buy
   */
  static purchaseItem(item, button) {
    const wasPayer = DDNA.tracking.getDataCapture().getPlayerCharacterizationParam('realMoneyPurchasesMade');

    OMT.platformTracking.logEvent(OMT.platformTracking.Events.IAPRequest, null, {
      price: item.price,
      productID: item.productID,
      priceCurrencyCode: item.priceCurrencyCode,
    });

    OMT.payments.buyAndConsumeProduct(item, (product, purchase, success) => {
      if (product === null) {
        return;
      }
      if (success) {
        OMT.platformTracking.logEvent(OMT.platformTracking.Events.IAPSuccess, null, {
          price: item.price,
          productID: item.productID,
          priceCurrencyCode: item.priceCurrencyCode,
        });

        OMT.platformTracking.logEvent(OMT.platformTracking.Events.IAPurchase, item.price, {
          package_id: item.productID,
          value: item.price,
          conversion: wasPayer ? 0 : 1,
        });
        this.processPurchase(product, purchase, button);
      }
    });
  }

  /**
   * process purchase
   * @param {Object} product product data
   * @param {Object} purchase purchase data
   * @param {G.Button} button
   */
  static processPurchase(product, purchase, button) {
    const reward = OMT.payments.parseRewards(product);
    G.saveState.incrementIAPCount();

    const transactionID = purchase && purchase.paymentID ? purchase.paymentID : '';

    // capture player stats
    if (DDNA.tracking.enabled) {
      const dataCapture = DDNA.tracking.getDataCapture();

      if (G.saveState.getLives() === 0) dataCapture.addToPlayerCharacterizationParam('zeroLivesRealMoneyPurchases', 1, false);
      dataCapture.addToPlayerCharacterizationParam('realMoneySpent', DDNA.utils.formatCurrencyValueAsInteger(product.price) / 100, false);
      dataCapture.addToPlayerCharacterizationSessionParam('realMoneySpentThisSession', DDNA.utils.formatCurrencyValueAsInteger(product.price) / 100);
      dataCapture.addToPlayerCharacterizationParam('coinValueBoughtWithRealMoney', reward.coins, false);
      dataCapture.addToPlayerCharacterizationParam('realMoneyPurchasesMade', 1, true);

      // track the transaction
      DDNA.transactionHelper.trackCoinShopPurchase(OMT.payments.convertToOldProductData(reward).giftData, {
        transactionType: 'PURCHASE',
        tActionType: 'INVENTORY_PURCHASE',
        tGameArea: game.state.getCurrentState().key === 'Game' ? 'LEVEL' : 'MAP',
        transactionID,
      }, product);
    }

    // dispatch a event to notify other game areas of purchase
    G.sb('onHandlePurchase').dispatch(product, OMT.payments.convertToOldProductData(reward));
    G.sfx.cash_register.play();

    // const button = this.findButtonWithID(product.productID);
    let x = 0;
    let y = 0;
    if (button !== null) {
      x = game.world.bounds.x + button.worldPosition.x;
      y = button.worldPosition.y;
    }

    for (let i = 0; i < reward.booster.length; i++) {
      G.gift.applyGift([reward.booster[i].item, reward.booster[i].amount], false); // Get booster
    }

    if (reward.coins > 0) {
      if (game.state.getCurrentState().key === 'World') { // we need to animate coins in the wold scene
        game.state.getCurrentState().uiTargetParticles.createCoinBatch( // Particles
          x,
          y,
          game.state.getCurrentState().panel.coinsTxt,
          reward.coins,
          true,
          null,
          game.state.getCurrentState().panel.coinsTxt,
        );
      } else { // other scenes we need to show the coinbar overlay as it is not part of the scene
        Shop_CoinBar.getInstance().show(true);
        game.state.getCurrentState().uiTargetParticles.createCoinBatch( // Particles
          x,
          y,
          Shop_CoinBar.getInstance().coinsTxt,
          reward.coins,
          true, // skip adding coins
          () => {
            // we want to dispatch the change event only after the coins have animated
            Shop_CoinBar.getInstance().hide(true);
          },
          Shop_CoinBar.getInstance().coinsTxt,
        );
      }
    }
  }
}
